import React, { useEffect, useMemo, useRef, useState } from "react";
import { Hooks, Util } from "@xt/xtbase";
import { observer } from "mobx-react-lite";
import { useStore } from "@/models";
import { futuresPathFromSymbol } from "@/components/Pages/AssetsTrading/utils";
import { get_ticker24h } from "@/api/v4/market";
import { get_hotSearchMarket } from "@/api/xt-app";
import ChangePill from "@/components/Pages/Market/MarketsPc/ChangePill";
import {
  formatUsdPrice,
  parseSymbolParts,
} from "@/components/Pages/Market/MarketsPc/utils";
import { resolveAssetFullName } from "@/components/Pages/Market/MarketsPc/coinFullNames";
import {
  DEFAULT_TRENDING_BASES,
  normalizeToCfdSymbol,
  resolveTrendingSymbols,
  searchMarketsFromConfig,
} from "@/utils/marketSearch";
import { buildSearchPages } from "./searchPages";
import { IconSearch, IconTrendUp, IconX } from "./icons";
import styles from "./SearchPcView.module.less";

const { useTranslation } = Hooks;
const { getUrl } = Util;

function SearchPcView() {
  const t = useTranslation();
  const { market, fund } = useStore();
  const inputRef = useRef(null);
  const [query, setQuery] = useState("");
  const [tickers, setTickers] = useState([]);
  const [trendingSymbols, setTrendingSymbols] = useState([]);

  const keyword = query.trim();
  const pages = useMemo(() => buildSearchPages(getUrl), []);

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  useEffect(() => {
    let cancelled = false;
    get_ticker24h()
      .then((data) => {
        if (!cancelled) setTickers(Array.isArray(data) ? data : []);
      })
      .catch(() => {
        if (!cancelled) setTickers([]);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    const { marketConfig } = market;
    if (!marketConfig || !Object.keys(marketConfig).length) return;

    let cancelled = false;
    (async () => {
      try {
        const hot = await get_hotSearchMarket();
        if (cancelled) return;
        if (Array.isArray(hot) && hot.length) {
          const fromHot = hot
            .map((item) => item.marketName || item.symbol || item)
            .map((sym) => normalizeToCfdSymbol(sym, marketConfig))
            .filter(Boolean)
            .slice(0, 5);
          if (fromHot.length) {
            setTrendingSymbols(fromHot);
            return;
          }
        }
      } catch {
        /* fallback below */
      }
      if (!cancelled) {
        setTrendingSymbols(resolveTrendingSymbols(marketConfig, DEFAULT_TRENDING_BASES));
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [market.marketConfig]);

  const coins = useMemo(() => {
    if (!keyword) return [];
    return searchMarketsFromConfig({
      marketConfig: market.marketConfig,
      tickerRecords: tickers,
      keyword,
      fund,
      dedupeByBase: true,
      cfdOnly: true,
      limit: 10,
    });
  }, [keyword, market.marketConfig, tickers, fund]);

  const pageHits = useMemo(() => {
    if (!keyword) return [];
    const k = keyword.toLowerCase();
    return pages.filter((p) => p.label.toLowerCase().includes(k));
  }, [keyword, pages]);

  const trendingItems = useMemo(() => {
    const { marketConfig } = market;
    if (!marketConfig) return [];
    const symbols =
      trendingSymbols.length > 0
        ? trendingSymbols
        : resolveTrendingSymbols(marketConfig, DEFAULT_TRENDING_BASES);
    const tickerMap = Object.fromEntries(tickers.map((row) => [row.s, row]));
    return symbols.map((symbol) => {
      const { base } = parseSymbolParts(symbol);
      return {
        symbol,
        base: base.toUpperCase(),
        doc: tickerMap[symbol] || { s: symbol },
      };
    });
  }, [market.marketConfig, trendingSymbols, tickers]);

  const handleTrendingClick = (base) => {
    setQuery(base);
    inputRef.current?.focus();
  };

  const handleMarketRowClick = (symbol) => {
    if (!symbol) return;
    window.location.href = getUrl(futuresPathFromSymbol(symbol));
  };

  return (
    <div className={styles.page}>
      <div className={styles.inputWrap}>
        <IconSearch className={styles.inputIcon} />
        <input
          ref={inputRef}
          type="search"
          className={styles.input}
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder={t("search.pcPlaceholder") || "Search coins, markets, pages…"}
          autoComplete="off"
        />
        {query ? (
          <button
            type="button"
            className={styles.clearBtn}
            onClick={() => setQuery("")}
            aria-label="Clear"
          >
            <IconX />
          </button>
        ) : null}
      </div>

      {!keyword && (
        <div className={styles.trendingBlock}>
          <div className={styles.trendingHead}>
            <IconTrendUp className={styles.trendingIcon} />
            {t("search.trending") || "Trending"}
          </div>
          <div className={styles.trendingList}>
            {trendingItems.map((item) => {
              const rawLogo = fund?.currencyMap?.[parseSymbolParts(item.symbol).base]?.logo;
              return (
                <button
                  key={item.symbol}
                  type="button"
                  className={styles.trendingChip}
                  onClick={() => handleTrendingClick(item.base)}
                >
                  {rawLogo ? (
                    <img src={rawLogo} alt="" className={styles.trendingCoinIcon} />
                  ) : (
                    <span className={styles.trendingCoinFallback}>{item.base.slice(0, 1)}</span>
                  )}
                  {item.base}
                </button>
              );
            })}
          </div>
        </div>
      )}

      {keyword ? (
        <div className={styles.results}>
          {coins.length > 0 && (
            <section className={styles.section}>
              <h3 className={styles.sectionTitle}>{t("search.markets") || "Markets"}</h3>
              <ul className={styles.resultCard}>
                {coins.map((doc) => {
                  const { base, quote } = parseSymbolParts(doc.s);
                  const displayBase = fund.getCurrencyDisplayName?.(base) || base.toUpperCase();
                  const displayQuote = fund.getCurrencyDisplayName?.(quote) || quote.toUpperCase();
                  const rawLogo = fund?.currencyMap?.[base]?.logo;
                  return (
                    <li key={doc.s}>
                      <button
                        type="button"
                        className={styles.marketRow}
                        onClick={() => handleMarketRowClick(doc.s)}
                      >
                        {rawLogo ? (
                          <img src={rawLogo} alt="" className={styles.rowIcon} />
                        ) : (
                          <span className={styles.rowIconFallback}>{base.slice(0, 1).toUpperCase()}</span>
                        )}
                        <div className={styles.rowMeta}>
                          <div className={styles.rowSymbol}>
                            {displayBase}
                            <span className={styles.rowQuote}>/{displayQuote}</span>
                          </div>
                          <div className={styles.rowName}>{resolveAssetFullName(base, fund)}</div>
                        </div>
                        <span className={styles.rowPrice}>{formatUsdPrice(doc.c, doc.bp)}</span>
                        <span className={styles.rowChange}>
                          <ChangePill doc={doc} subtle />
                        </span>
                      </button>
                    </li>
                  );
                })}
              </ul>
            </section>
          )}

          {pageHits.length > 0 && (
            <section className={styles.section}>
              <h3 className={styles.sectionTitle}>{t("search.pages") || "Pages"}</h3>
              <ul className={styles.resultCard}>
                {pageHits.map((page) => (
                  <li key={page.href}>
                    <a href={page.href} className={styles.pageRow}>
                      {page.label}
                    </a>
                  </li>
                ))}
              </ul>
            </section>
          )}

          {coins.length === 0 && pageHits.length === 0 && (
            <div className={styles.emptyCard}>
              {t("search.noResults", { q: query }) || `No results for “${query}”.`}
            </div>
          )}
        </div>
      ) : null}
    </div>
  );
}

export default observer(SearchPcView);
